
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/ui/Input';
import Logo from '../components/Logo';
import { useToast } from '../contexts/ToastContext';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
        await sendPasswordResetEmail(auth, email);
        setIsSent(true);
        showToast('再設定メールを送信しました', 'success');
    } catch {
        showToast('送信に失敗しました。', 'error');
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
          <div className="flex justify-center mb-12 lg:hidden">
              <Logo size="lg" />
          </div>

          <h1 className="text-2xl font-bold text-slate-800 mb-10 text-center">パスワード再設定</h1>
          
          {isSent ? (
              <div className="text-center animate-in">
                  <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 border border-blue-100">
                      <CheckCircle2 size={32} />
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-10 font-bold">
                      ご入力いただいたメールアドレス宛に、<br/>パスワード再設定用のリンクを送信しました。
                  </p>
                  <button 
                    onClick={() => navigate('/login')}
                    className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl shadow-lg flex items-center justify-center active:scale-95"
                  >
                    ログイン画面に戻る
                  </button>
              </div>
          ) : (
              <form onSubmit={handleSubmit}>
                <p className="text-xs text-slate-500 mb-8 leading-relaxed text-center font-bold">
                    ご登録時のメールアドレスを入力してください。<br/>パスワード再設定の案内を送信します。
                </p>
                
                <Input 
                  label="メールアドレス" 
                  type="email" 
                  placeholder="sample@email.com"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                />

                <button 
                    type="submit" 
                    disabled={isLoading || !email} 
                    className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                    {isLoading ? <Loader2 className="animate-spin" size={20} /> : '再設定メールを送信'}
                </button>

                <div 
                    onClick={() => navigate('/login')}
                    className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400 font-bold cursor-pointer hover:text-slate-600 transition-colors"
                >
                    <ArrowLeft size={16} />
                    戻る
                </div>
              </form>
          )}
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
